/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SelectOption } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { TaskGoal } from '../../modules/admin/tasks/tasks.types';
import { NextTaskTypeGoal, NextTaskTypeGoalDeliveryMode } from '../../core/task-type/task-type.types';
import { LocalizePipe } from '../../core/pipes/localize.pipe';
import { MasterDataService } from '../../core/master-data/master-data.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { Utils } from '../../core/utils/utils';
import { FuseConfirmationDialogComponent } from '../../../@fuse/services/confirmation/dialog/dialog.component';

@Component({
    selector: 'app-task-goal-dialog',
    templateUrl: './task-goal-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskGoalDialogComponent implements OnInit, OnDestroy {

    goals: TaskGoal[] = [];
    goalForm: FormArray<FormGroup<{
        goalName: FormControl<string>;
        goalValue: FormControl<number>;
        masterName: FormControl<string>;
        masterOid: FormControl<string | SelectOption>;
        order: FormControl<number>;
        taskId: FormControl<string>;
        valuePercent: FormControl<number>;
        valueInUnits: FormControl<number>;
    }>> = new FormArray([]);
    selectOptionsByUri = new Map<string, SelectOption[]>();
    filteredOptionsByUri = new Map<string, SelectOption[]>();

    unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            taskTypeGoals: NextTaskTypeGoal[];
            goals: TaskGoal[];
            fromEditTask: boolean;
        },
        public dialogRef: MatDialogRef<TaskGoalDialogComponent>,
        private dialogsService: DialogsService,
        private _fuseConfirmationService: FuseConfirmationService,
        private localize: LocalizePipe,
        private masterService: MasterDataService,
        private transloco: TranslocoService
    ) { }

    async ngOnInit() {
        // No había objetivos o no se habían rellenado todos
        let taskGoal: TaskGoal;
        let selectOptions: SelectOption[];
        let valueValidators: ValidatorFn[];
        for (const taskTypeGoal of this.data.taskTypeGoals) {
            if (taskTypeGoal.fromMaster && !this.selectOptionsByUri.has(taskTypeGoal.masterUri)) {
                selectOptions = await firstValueFrom(this.masterService.retrieveMasterDataAsSelectOptions(taskTypeGoal.masterUri));
                this.selectOptionsByUri.set(taskTypeGoal.masterUri, selectOptions);
                this.filteredOptionsByUri.set(taskTypeGoal.masterUri, selectOptions);
            }
            taskGoal = this.data.goals.find(g => g.goalName === taskTypeGoal.name);
            if (!taskGoal) {
                taskGoal = {
                    goalName: taskTypeGoal.name,
                };
            }
            valueValidators = [Validators.min(0)];
            if (taskTypeGoal.deliveryMode === NextTaskTypeGoalDeliveryMode.percent) {
                valueValidators.push(Validators.max(100));
            }
            this.goals.push(taskGoal);
            const formGroup = new FormGroup({
                goalName: new FormControl<string>(taskGoal.goalName),
                goalValue: new FormControl<number>(taskGoal.goalValue, valueValidators),
                masterName: new FormControl<string>(taskGoal.masterName),
                masterOid: new FormControl<string | SelectOption>(this.selectOptionsByUri.get(taskTypeGoal.masterUri)?.find(option => option.code === taskGoal.masterOid)),
                order: new FormControl<number>(taskGoal.order),
                taskId: new FormControl<string>(taskGoal.taskId),
                valueInUnits: new FormControl<number>(taskGoal.valueInUnits),
                valuePercent: new FormControl<number>(taskGoal.valuePercent)
            });
            formGroup.get('masterOid').valueChanges
                .pipe(takeUntil(this.unsubscribeAll))
                .subscribe(value => {
                    if (typeof value === 'string') {
                        this.filteredOptionsByUri.set(taskTypeGoal.masterUri, selectOptions.filter(option => Utils.removeAccents(this.localize.transform(option.value).toUpperCase()).includes(Utils.removeAccents(value.toUpperCase()))));
                    } else {
                        this.filteredOptionsByUri.set(taskTypeGoal.masterUri, selectOptions);
                    }
                });
            this.goalForm.push(formGroup);
        };
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    displayFn = (option: SelectOption) => {
        return option ? this.localize.transform(option.value) : '';
    }

    save() {
        const confirmDialog = this.openConfirmDialog(this.data.fromEditTask ? this.transloco.translate('goal.confirmSaveFromEdit') : this.transloco.translate('goal.confirmSave'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                this.goalForm.markAllAsTouched();
                if (!this.goalForm.valid) {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('errorsInForm')
                    });
                    return;
                }
                const goals: TaskGoal[] = this.goalForm.value.map(goal => {
                    return { ...goal, masterOid: typeof goal.masterOid === 'string' ? goal.masterOid : goal.masterOid?.code };
                });
                this.closeDialog(goals.filter(goal => goal.goalValue !== undefined && goal.goalValue !== null));
            }
        });
    }

    closeDialog(result?: any): void {
        this.dialogRef.close(result);
    }

    private openConfirmDialog(message: string): MatDialogRef<FuseConfirmationDialogComponent, any> {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

}
